import classNames from 'classnames';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Table } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import { doFormMsg, FormMsg, NoFormMsg } from 'components/forms/FormMessage';
import Icon from 'components/icons/Icon';
import { IconSymbol, IconVariant } from 'components/icons/Icon.types';
import {
  ModalDialog,
  ModalDialogLink,
  ModalFooter,
} from 'components/modal/Modal';
import { useModal } from 'components/modal/ModalContext';
import UserAvatar from 'components/user/UserAvatar';
import { IUser, Role, RoleType } from 'features/user/userAPI';
import { ISignupData, signupUserAsync } from 'features/user/userSlice';
import { fetchUsersAsync, modifyUserAsync } from 'features/users/usersSlice';
import { hideEmail } from 'helpers/helpers';
import { sortBy, sortById } from 'helpers/sorting';
import { useUsers } from 'selectors/usersSelectors';
import { paginateItems, Paginator } from './Paginator';
import UserDataForm, { UserSettings } from './UserDataForm';
import { DialogHeader } from './helpers/DialogHeader';
import { SortArrows } from './helpers/SortArrows';

import './UsersForm.scss';

export default function UsersForm() {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { users } = useUsers();
  const { openModal } = useModal();

  const [currentPage, setCurrentPage] = useState(1);
  const [editing, setEditing] = useState<Set<number>>(new Set());
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>([]);
  const [searchItem, setSearchItem] = useState<string>('');
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  const [allItems, setAllItems] = useState(false);
  const [showIds, setShowIds] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(true);
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [sort, setSort] = useState<{
    column: string;
    direction: 'ASC' | 'DESC';
  }>({ column: 'id', direction: 'ASC' });

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        dispatch(fetchUsersAsync()).then((response: { payload: any }) => {
          switch (response.payload?.response?.status) {
            case 200:
              break;
            default:
              break;
          }
        }),
      ]);
      setReload(false);
    };
    if (reload) {
      loadData();
    }
  }, [dispatch, reload]);

  useEffect(() => {
    if (searchItem && searchItem.length > 0)
      setFilteredUsers(
        users.filter(
          (u: IUser) =>
            u.username?.toLowerCase().includes(searchItem.toLowerCase()) ||
            u.email?.toLowerCase().includes(searchItem.toLowerCase()),
        ),
      );
    else setFilteredUsers(users);
  }, [searchItem, users]);

  function rowEditing(start: boolean, id: number) {
    setEditing((prev) => {
      // Create a new Set to trigger rerender
      const newSet = new Set(prev);
      if (start) {
        newSet.add(id);
      } else {
        newSet.delete(id);
        setReload(true);
      }
      return newSet;
    });
  }

  function handleNewUser(userData: UserSettings): boolean {
    const newUser: ISignupData = {
      username: userData.username,
      email: userData.email,
      password: userData.password ?? '',
    };
    try {
      dispatch(signupUserAsync({ data: newUser, noEmail: true })).then(
        (response: { payload: any }) => {
          switch (response.payload?.response?.status) {
            case 201:
              dispatch(
                modifyUserAsync({
                  data: {
                    ...newUser,
                    id: response.payload.response.data._id,
                    isVerified: true, // TODO: consider false
                  },
                  newUser: true,
                }),
              );
              setReload(true);
              break;
            default:
              doFormMsg(
                {
                  message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
                  success: false,
                },
                setFormMsg,
              );
              break;
          }
        },
      );
      return true;
    } catch (err) {
      doFormMsg(
        {
          message: intl.formatMessage({ id: 'X.FAILED_UNKNOWN' }),
          success: false,
        },
        setFormMsg,
      );
      console.log(
        'Project update or post request failed for unclear reason.',
        err,
      );
      return false;
    }
  }

  // Sorting handlers
  const handleSortAsc = useCallback((column: string) => {
    setSort({ column, direction: 'ASC' });
  }, []);
  const handleSortDesc = useCallback((column: string) => {
    setSort({ column, direction: 'DESC' });
  }, []);

  // Sort filteredUsers by ID according to sortId
  const sortedUsers = React.useMemo(() => {
    if (sort.column === 'role') {
      const sorted = sortById(filteredUsers);
      sorted.sort((a, b) => {
        const roleA = a.roles.includes(Role.admin)
          ? 1
          : a.roles.includes(Role.manager)
            ? 2
            : a.roles.includes(Role.user)
              ? !!a.email
                ? 3
                : 4
              : 4;
        const roleB = b.roles.includes(Role.admin)
          ? 1
          : b.roles.includes(Role.manager)
            ? 2
            : b.roles.includes(Role.user)
              ? !!b.email
                ? 3
                : 4
              : 4;
        if (roleA > roleB) return sort.direction === 'ASC' ? -1 : 1;
        if (roleA < roleB) return sort.direction === 'ASC' ? 1 : -1;
        return a.id - b.id;
      });
      return sorted;
    }
    return sortBy(filteredUsers, sort.column, sort.direction);
  }, [filteredUsers, sort]);

  return (
    <ModalDialog
      className="settings_form users_form wide"
      title={
        <DialogHeader
          title={<FormattedMessage id={'USERS.FORM.TITLE'} />}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          showIds={showIds}
          setShowIds={setShowIds}
          searchItem={searchItem}
          setSearchItem={setSearchItem}
          allItems={allItems}
          setAllItems={setAllItems}
        />
      }
      disableClose={editing.size > 0}
      enableEscape={false}
      formMsg={formMsg}
      width="max"
      height="max"
      editing
    >
      <ModalFooter>
        <div className="left">
          <div>
            <Icon
              symbol={IconSymbol.reload}
              variant={reload ? IconVariant.light : IconVariant.normal}
              hoverVariant={IconVariant.dark}
              size={18}
              inline
              onClick={() => setReload(true)}
              debug
            />{' '}
            {reload ? (
              <FormattedMessage id="GENERAL.RELOADING" />
            ) : editing.size > 0 ? (
              <FormattedMessage
                id="GENERAL.FORM.ROWS_EDITING"
                values={{ count: editing.size }}
              />
            ) : null}
          </div>
          <FormattedMessage
            id="USERS.FORM.USER_COUNT"
            values={{ count: filteredUsers.length }}
          />
        </div>
        <Paginator
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemCount={filteredUsers.length}
          itemsPerPage={itemsPerPage}
          allItems={allItems}
        />
        <ModalDialogLink
          component={UserDataForm}
          componentProps={{ editedUser: undefined, onSubmit: handleNewUser }}
          asButton
          className="right"
        >
          <FormattedMessage id="USERS.FORM.ADD_USER" />
        </ModalDialogLink>
      </ModalFooter>
      <Table bordered size="sm" className="users_table">
        <TableHead
          sort={sort}
          onSortAsc={handleSortAsc}
          onSortDesc={handleSortDesc}
        />
        <TableBody
          currentPage={currentPage}
          users={sortedUsers}
          itemsPerPage={allItems ? 9999 : itemsPerPage}
          rowEditing={rowEditing}
          showIds={showIds}
          setFormMsg={setFormMsg}
        />
      </Table>
    </ModalDialog>
  );
}

function TableHead({
  onSortAsc,
  onSortDesc,
  sort,
}: {
  onSortAsc?: (column: string) => void;
  onSortDesc?: (column: string) => void;
  sort?: { column: string; direction: 'ASC' | 'DESC' };
} = {}) {
  return (
    <thead className="users_thead">
      <tr>
        <th style={{ width: '6%', paddingLeft: '30px' }}>
          <FormattedMessage id="X.ID" />
          <SortArrows
            direction={sort?.column === 'id' ? sort.direction : undefined}
            onAsc={() => onSortAsc && onSortAsc('id')}
            onDesc={() => onSortDesc && onSortDesc('id')}
            column="ID"
          />
        </th>
        <th style={{ width: 'auto' }}>
          <FormattedMessage id="USERS.FORM.NAME" />
          <SortArrows
            direction={sort?.column === 'username' ? sort.direction : undefined}
            onAsc={() => onSortAsc && onSortAsc('username')}
            onDesc={() => onSortDesc && onSortDesc('username')}
            column="NAME"
          />
        </th>
        <th style={{ width: '10%' }}>
          <FormattedMessage id="USERS.FORM.EMAIL" />
          <SortArrows
            direction={sort?.column === 'email' ? sort.direction : undefined}
            onAsc={() => onSortAsc && onSortAsc('email')}
            onDesc={() => onSortDesc && onSortDesc('email')}
            column="EMAIL"
          />
        </th>
        <th style={{ width: '6%' }}>
          <FormattedMessage id="USERS.FORM.ROLE" />
          <SortArrows
            direction={sort?.column === 'role' ? sort.direction : undefined}
            onAsc={() => onSortAsc && onSortAsc('role')}
            onDesc={() => onSortDesc && onSortDesc('role')}
            column="ROLE"
          />
        </th>
        <th style={{ width: '10%' }}>
          <FormattedMessage id="USERS.FORM.LAST_LOGIN" />
          <SortArrows
            direction={
              sort?.column === 'lastLogin' ? sort.direction : undefined
            }
            onAsc={() => onSortAsc && onSortAsc('lastLogin')}
            onDesc={() => onSortDesc && onSortDesc('lastLogin')}
            column="LAST_LOGIN"
          />
        </th>
        <th style={{ width: '6%' }}></th>
      </tr>
    </thead>
  );
}

type TableRowProps = {
  user: IUser;
  rowEditing: (start: boolean, id: number) => void;
  showIds: boolean;
  setFormMsg: Dispatch<SetStateAction<FormMsg>>;
};

const TableRow = React.memo(function TableRow(props: TableRowProps) {
  const { user, rowEditing, showIds, setFormMsg } = props;
  const [changesTriggered, setChangesTriggered] = useState<Boolean>(false);
  const dispatch = useAppDispatch();
  const [rowData, setRowData] = useState<IUser>(user);

  function editingRow(editing: boolean) {
    setChangesTriggered(editing);
    rowEditing(editing, rowData.id ?? -1);
  }

  function handleSubmitUserSettings(userData: UserSettings): boolean {
    const roles: RoleType[] = [Role.user];
    if (userData.admin) {
      roles.push(Role.admin);
    }
    if (userData.manager) {
      roles.push(Role.manager);
    }
    setRowData((prevData) => ({
      ...prevData,
      username: userData.username,
      email: userData.email,
      isVerified: userData.isVerified,
      roles: roles,
    }));
    editingRow(true);
    return true;
  }

  function handleReset() {
    setRowData(user);
    editingRow(false);
  }

  function handleSave() {
    if (rowData.id) {
      dispatch(modifyUserAsync({ data: rowData, newUser: false })).then(
        (response: { payload: any }) => {
          switch (response.payload?.response.status) {
            case 200:
            case 201:
              doFormMsg(
                {
                  message: 'User updated',
                  success: true,
                  timeout: 1500,
                },
                setFormMsg,
              );
              break;
            case 500:
            default:
              const m = 'Internal error, user update not saved.';
              doFormMsg(
                {
                  message: m,
                  success: false,
                },
                setFormMsg,
              );
              break;
          }
        },
      );
    }
    editingRow(false);
  }

  return (
    <>
      <tr
        className={classNames('user_row', {
          user_modified: changesTriggered,
        })}
      >
        <td className="user_avatar_id">
          <div className="user_id">{rowData.id}</div>
          <UserAvatar user={user} small inline />
        </td>
        <td className="user_name">
          <ModalDialogLink
            component={UserDataForm}
            componentProps={{
              editedUser: rowData,
              onSubmit: (ud) => {
                handleSubmitUserSettings(ud);
                setChangesTriggered(true);
                return true;
              },
            }}
          >
            {rowData.username}
          </ModalDialogLink>
        </td>
        <td className="user_email">
          <ModalDialogLink
            component={UserDataForm}
            componentProps={{
              editedUser: rowData,
              onSubmit: handleSubmitUserSettings,
            }}
          >
            {hideEmail(rowData.email!)}
          </ModalDialogLink>
        </td>
        <td
          className={classNames('user_role', {
            // TODO: add style for inconsistent subscription when a moderator is not subscribed to the dialogue/project
            user_unverified: !rowData.isVerified,
          })}
        >
          <ModalDialogLink
            component={UserDataForm}
            componentProps={{
              editedUser: rowData,
              onSubmit: handleSubmitUserSettings,
            }}
          >
            {rowData.roles?.includes(Role.admin)
              ? Role.admin
              : rowData.roles?.includes(Role.manager)
                ? Role.manager
                : rowData.email?.length
                  ? Role.user
                  : 'guest'}
          </ModalDialogLink>
        </td>
        <td className="user_lastlogin">
          {/* {(rowData.lastLogin || '---').toLocaleString()} */}
          {rowData.lastLogin
            ? new Date(rowData.lastLogin).toLocaleString()
            : '---'}
        </td>
        <td>
          <div className="user_controls">
            <Icon
              className={changesTriggered ? 'user_changed' : 'user_unchanged'}
              symbol={IconSymbol.check}
              onClick={handleSave}
              size={24}
              disabled={!changesTriggered}
              variant={IconVariant.accent}
              inline
              hintProps={{
                hint: { id: 'X.SAVE' },
              }}
            />
            <Icon
              className={changesTriggered ? 'user_changed' : 'user_unchanged'}
              symbol={IconSymbol.checkNot}
              onClick={handleReset}
              size={24}
              disabled={!changesTriggered}
              variant={IconVariant.dark}
              inline
              hintProps={{
                hint: { id: 'X.CANCEL' },
              }}
            />
          </div>
        </td>
      </tr>
    </>
  );
});

type TableBodyProps = {
  currentPage: number;
  users: IUser[];
  itemsPerPage: number;
  rowEditing: (start: boolean, id: number) => void;
  showIds: boolean;
  setFormMsg: Dispatch<SetStateAction<FormMsg>>;
};
function TableBody(props: TableBodyProps) {
  const { currentPage, itemsPerPage, rowEditing, users, showIds, setFormMsg } =
    props;

  const paginatedUsers = paginateItems(users, currentPage, itemsPerPage);

  return (
    <tbody>
      {paginatedUsers.map((user: IUser) => (
        <TableRow
          key={user.id}
          user={user}
          rowEditing={rowEditing}
          showIds={showIds}
          setFormMsg={setFormMsg}
        />
      ))}
    </tbody>
  );
}
